/**
 * Category tag — coloured swatch + i18n label for a `categoryId`.
 *
 * Swatch class strings live in `model/category-meta.ts` (single source of
 * truth, drift-tested against `taxonomy.ts`). The label resolves via i18n
 * (`categoryMeta(id).labelKey` from taxonomy). Unknown `categoryId` silently
 * degrades to the neutral fallback swatch + raw id — protects the table
 * render path if a backend ever widens the taxonomy without a frontend bump
 * (Zod catches first at the boundary).
 *
 * No inline `style` props — see `memory/feedback_no_inline_style.md`.
 */

import { useTranslation } from 'react-i18next'
import { categoryMeta } from '@/features/accounting/api/taxonomy'
import { CATEGORY_META, UNKNOWN_SWATCH_CLASS } from '@/features/accounting/model/category-meta'
import type { CategoryId } from '@/features/accounting/api/contracts'

interface CategoryTagProps {
  categoryId: CategoryId
}

export function CategoryTag({ categoryId }: CategoryTagProps) {
  const { t } = useTranslation('accounting')
  const meta = categoryMeta(categoryId)
  const swatchClass = meta ? CATEGORY_META[categoryId].swatchClass : UNKNOWN_SWATCH_CLASS
  return (
    <span className='inline-flex items-center gap-2 text-sm'>
      <span
        aria-hidden='true'
        className={['inline-block h-2.5 w-2.5 rounded-sm', swatchClass].join(' ')}
      />
      <span>{meta ? t(meta.labelKey) : categoryId}</span>
    </span>
  )
}
